<template lang="pug">
    .chart-container.siteviews-chart
        .header-row
            el-input.widget-title-input(
                v-if="dashboardEditMode"
                :value="widget.customName || ''"
                :placeholder="$t(widget.name)"
                size="mini"
                @input="onCustomNameChange"
            )
            h3.title(v-else) {{ displayName }}
            span.spinner(v-if="loading")
            a.force-update-icon(
                v-else-if="!loading && !(error && [401, 403].includes(error.code))"
                href="#"
                @click.prevent="forceRefetch"
            )
                i.el-icon-fa-sync-alt.fas
            template(v-if="!(error && [401, 403].includes(error.code))")
                el-form.filters.filters-inline(
                    label-position="left"
                    size="mini"
                )
                    .dynamic-width-field
                        .occupy-width {{ domain }}
                        el-select.filter-select(
                            placeholder=""
                            filterable
                            v-model="domain"
                            autocomplete="no-fill"
                            :disabled="domainOptions.length <= 1"
                        )
                            el-option(v-for="d in domainOptions" :key="d.label" :label="d.label" :value="d.label")
                    .dynamic-width-field
                        .occupy-width {{ periodLabel }}
                        el-select.filter-select(
                            placeholder=""
                            filterable
                            v-model="period"
                            autocomplete="no-fill"
                        )
                            el-option(v-for="p in periodOptions" :key="p.key" :label="p.label" :value="p.key")
                    .dynamic-width-field(v-if="period === 'year'")
                        .occupy-width {{ dateDisplayLabel }}
                        el-select.filter-select(
                            v-model="selectedDecade"
                            placeholder=""
                            autocomplete="no-fill"
                        )
                            el-option(v-for="d in decadeOptions" :key="d.value" :label="d.label" :value="d.value")
                    .dynamic-width-field(v-else)
                        .occupy-width {{ dateDisplayLabel }}
                        el-date-picker.filter-date(
                            v-model="date"
                            :type="datepickerType"
                            size="mini"
                            :format="datepickerFormat"
                            prefix-icon="far el-icon-fa-calendar-alt"
                            :unlink-panels="true"
                            :clearable="false"
                            :picker-options="{ firstDayOfWeek:1, disabledDate }"
                        )
                .filter-dropdown
                    el-popover(
                        trigger="click"
                        placement="bottom-end"
                        :append-to-body="false"
                        popper-class="filter-dropdown-popover"
                    )
                        el-form.filters(
                            ref="form"
                            label-position="top"
                            size="mini"
                        )
                            el-form-item.no-margin(:label="$t('Domain')")
                                el-select(
                                    placeholder=""
                                    filterable
                                    v-model="domain"
                                    autocomplete="no-fill"
                                    :disabled="domainOptions.length <= 1"
                                )
                                    el-option(v-for="d in domainOptions" :key="d.label" :label="d.label" :value="d.label")
                            .grow
                            el-form-item.no-margin(:label="$t('Daterange')")
                                el-date-picker(
                                    v-model="date"
                                    :type="datepickerType"
                                    size="mini"
                                    :format="datepickerFormat"
                                    prefix-icon="far el-icon-fa-calendar-alt"
                                    :unlink-panels="true"
                                    :clearable="false"
                                    :picker-options="{ firstDayOfWeek:1, disabledDate }"
                                )
                            el-form-item.no-margin(:label="$t('Period')")
                                el-select(placeholder="" filterable v-model="period" autocomplete="no-fill")
                                    el-option(v-for="p in periodOptions" :key="p.key" :label="p.label" :value="p.key")
                        el-button.filter-toggle-button(slot="reference" size="mini")
                            i.fas.el-icon-fa-sliders-h
        .error(v-if="error")
            template(v-if="error.code")
                Error(:message="$t(error.message)" :code="error.code")
            template(v-else)
                Error(:message="$t('Something went wrong!')" :code="501")
        .pagination-wrap(v-else)
            el-button.prev(
                v-if="!loading"
                size="mini"
                type="primary"
                icon="el-icon-arrow-left"
                circle
                @click="prev"
            )
            el-button.next(
                v-if="!loading"
                size="mini"
                type="primary"
                icon="el-icon-arrow-right"
                circle
                @click="next"
                :disabled="isNextDisabled"
            )
            //- :height="widget.settings.height"
            //- line-chart.chart(
            //-     ref="chart"
            //-     :chart-data="datacollection"
            //-     :options="chartOptions"
            //-     :style="{ 'max-height': `${widget.settings.height}px` }"
            //- )
            line-chart-extended.chart(
                ref="chart"
                :chart-data="datacollection"
                :options="chartOptions"
            )
        //- button(@click="generate") Randomize
        .legend(v-if="!error")
            ul.no-style-list
                li.all-visitors
                    el-button(
                        size="mini"
                        @click="toggleDatasets(['all-visitors'], $event)"
                    )
                        span.color(:style="getLegendStyle('all-visitors')")
                        span.label(:class="{ 'strike-through': disabledDatasets.includes('all-visitors') }")
                            | {{ $t('Unique Visitors') }}
                li.all-views
                    el-button(
                        size="mini"
                        @click="toggleDatasets(['all-pageviews'], $event)"
                    )
                        span.color(:style="getLegendStyle('all-pageviews')")
                        span.label(:class="{ 'strike-through': disabledDatasets.includes('all-pageviews') }")
                            | {{ $t('Siteviews') }}
            template(v-if="(campaigns.length && enabledExtensions.includes('marketing')) || marketplaceCampaign")
                hr
                .campaigns
                    .left
                        el-button-group
                            el-button(
                                size="mini"
                                :icon="campaignVisitorsVisible ? '' : 'el-icon-fa-eye-slash fas'"
                                :type="campaignVisitorsVisible ? 'default' : 'info'"
                                @click="toggleCampaignVisitors"
                            )
                                | {{ $t('Unique Visitors') }}
                            el-button(
                                size="mini"
                                :icon="campaignViewsVisible ? '' : 'el-icon-fa-eye-slash fas'"
                                :type="campaignViewsVisible ? 'define' : 'info'"
                                @click="toggleCampaignViews"
                            )
                                | {{ $t('Siteviews') }}
                    .right
                        ul.no-style-list
                            li.campaign(v-for="campaign in campaigns")
                                el-button(
                                    size="mini"
                                    @click="toggleDatasets([`${campaign.key}:visitors`, `${campaign.key}:views`], $event)"
                                )
                                    span.color(
                                        :class="{ disabled: campaignVisitorsVisible !== true }"
                                        :style="getLegendStyle(`${campaign.key}:visitors`, campaignVisitorsVisible !== true)"
                                    )
                                    span.secondary-color(
                                        :class="{ disabled: campaignViewsVisible !== true }"
                                        :style="getLegendStyle(`${campaign.key}:views`, campaignViewsVisible !== true)"
                                    )
                                    span.label(:class="{ 'strike-through': disabledDatasets.includes(`${campaign.key}:visitors`) || disabledDatasets.includes(`${campaign.key}:views`)}")
                                        | {{ campaign.name }}
                            li.divider(v-if="campaigns.length && marketplaceCampaign")
                            li.campaign(v-if="marketplaceCampaign")
                                el-button(
                                    size="mini"
                                    @click="toggleDatasets(['city-offer-marketplace:visitors', 'city-offer-marketplace:views'], $event)"
                                )
                                    span.color(
                                        :class="{ disabled: campaignVisitorsVisible !== true }"
                                        :style="getLegendStyle('city-offer-marketplace:visitors', campaignVisitorsVisible !== true)"
                                    )
                                    span.secondary-color(
                                        :class="{ disabled: campaignViewsVisible !== true }"
                                        :style="getLegendStyle('city-offer-marketplace:views', campaignViewsVisible !== true)"
                                    )
                                    span.label(:class="{ 'strike-through': disabledDatasets.includes('city-offer-marketplace:visitors') || disabledDatasets.includes('city-offer-marketplace:views')}")
                                        | {{ $t('CityOffer Marketplace') }}
</template>

<i18n>
{
    "de": {
        "CityOffer Marketplace": "CityOffer Marktplatz",
        "All Pages Report": "Aufrufe insgesamt",
        "Siteviews": "Seitenaufrufe",
        "Show Siteviews": "Seitenaufrufe anzeigen",
        "Hide Siteviews": "Seitenaufrufe verbergen",
        "Unique Visitors": "Besucher",
        "Show Visitors": "Besucher anzeigen",
        "Hide Visitors": "Besucher verbergen",
        "Daterange": "Zeitraum",
        "Period": "Intervall",
        "Hourly": "Stündlich",
        "Daily": "Täglich",
        "Weekly": "Wöchentlich",
        "Monthly": "Monatlich",
        "Yearly": "Jährlich",
        "Domain": "Domain",
        "Week": "Kalenderwoche",
        "The summary will be processed asynchronously": "Die Zusammenfassung wird asynchron verarbeitet",
        "You will be notified once the summary is completed": "Sie werden benachrichtigt, sobald die Zusammenfassung abgeschlossen ist"
    }
}
</i18n>

<script>
import Color from 'color'

import StatisticsApi from '@/store/api/statistics';

import isPast from 'date-fns/isPast';

import startOfHour from 'date-fns/startOfHour';
import endOfHour from 'date-fns/endOfHour';

import startOfDay from 'date-fns/startOfDay';
import endOfDay from 'date-fns/endOfDay';

import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';

import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';

import startOfYear from 'date-fns/startOfYear';
import endOfYear from 'date-fns/endOfYear';

import startOfDecade from 'date-fns/startOfDecade';
import endOfDecade from 'date-fns/endOfDecade';

import getUnixTime from 'date-fns/getUnixTime'
import fromUnixTime from 'date-fns/fromUnixTime'

import addHours from 'date-fns/addHours'
import subHours from 'date-fns/subHours'

import addDays from 'date-fns/addDays'
import subDays from 'date-fns/subDays'

import addWeeks from 'date-fns/addWeeks'
import subWeeks from 'date-fns/subWeeks'

import addMonths from 'date-fns/addMonths'
import subMonths from 'date-fns/subMonths'

import addYears from 'date-fns/addYears'
import subYears from 'date-fns/subYears'

import echo from '@/echo';
import filterOverflowMixin from '@/views/cms/Dashboard/widgets/filterOverflowMixin';
import { appNow, appGetUnixTime, appFromUnixTime } from '@/utils/timezone';

import differenceInHours from 'date-fns/differenceInHours'
import differenceInDays from 'date-fns/differenceInDays'
import differenceInWeeks from 'date-fns/differenceInWeeks'
import differenceInMonths from 'date-fns/differenceInMonths'
import differenceInYears from 'date-fns/differenceInYears'

import { adminClient as apolloClient } from '@/store/apollo';
import { formatDate } from '@/filters'
import { useAuthStore, useSetupStore, useDashboardStore } from '@/stores'

// import StatusLogApi from '@/store/api/system/statusLog';

import LineChart from '@/components/LineChart'
// import StatusLog from '@/views/cms/Dashboard/widgets/logs/Partials/StatusLog';
import LineChartExtended from '@/components/LineChartExtended'
import Error from '@/components/Error';

export default {
    name: 'SiteViewsChart',
    mixins: [filterOverflowMixin],
    props: {
        widget: {
            type: Object,
            required: true,
        },
        dashboardEditMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const component = this;
        return {
            loading: false,
            error: false,
            right: 'statistics:siteview-summaries',
            // windowWidth: undefined,
            datacollection: undefined,
            disabledDatasets: _.get(this.widget, 'settings.disabledDatasets') || [],
            campaignVisitorsVisible: _.get(this.widget, 'settings.campaignVisitorsVisible') !== undefined ? _.get(this.widget, 'settings.campaignVisitorsVisible') : true,
            campaignViewsVisible: _.get(this.widget, 'settings.campaignViewsVisible') !== undefined ? _.get(this.widget, 'settings.campaignViewsVisible') : true,
            chartOptions: {
                responsive: true,
                maintainAspectRatio: false,
                animation: {
                    duration: 1000,
                },
                plugins: {
                    tooltip: {
                        mode: 'nearest',
                        intersect: false,
                    },
                    legend: {
                        display: false,
                        position: 'bottom',
                        align: 'end',
                        labels: {
                            usePointStyle: true,
                        },
                    },
                },
                scales: {
                    x: {
                        offset: true,
                        grid: {
                            offset: true,
                            drawTicks: true,
                        },
                    },
                    y: {
                        beginAtZero: true,
                        ticks: {
                            precision: 0,
                        },
                        grid: {
                            drawTicks: true,
                        },
                    }
                },
                onHover: (event, elements) => {
                    if (elements.length && component.period !== 'hour') {
                        event.native.target.style.cursor = 'pointer';
                    } else {
                        event.native.target.style.cursor = 'default';
                    }
                },
                onClick: this.handleClick
            },

            // logKeys: ['summarize-siteview-statistics'],
            // loadingLogs: false,
            // logs: [],
        }
    },
    filters: {
        formatDate
    },
    components: {
        LineChart,
        // StatusLog,
        LineChartExtended,
        Error,
    },
    watch: {
        disabledDatasets: {
            deep: true,
            handler(value) {
                this.syncSettingsField('disabledDatasets', value);
            }
        },
        campaignVisitorsVisible(value) {
            this.syncSettingsField('campaignVisitorsVisible', value);
        },
        campaignViewsVisible(value) {
            this.syncSettingsField('campaignViewsVisible', value);
        },
        activeCms(cms) {
            const reload = _.get(cms, 'reloadComponents', true);
            this.chartOptions.animation.duration = 1000
            if (reload) this.refetch();
        },
        logs(newLogs, oldLogs) {
            if (oldLogs.length && ! newLogs.length) {
                // WARN: clearStore() is causing errors
                apolloClient.clearStore().then(() => {
                    setTimeout(() => this.refetch(false), 10);
                })

                // apolloClient.cache.reset()
                // this.refetch(false)
            }
        },
    },
    computed: {
        displayName() {
            return this.widget.customName || this.$t(this.widget.name);
        },
        marketplaceCampaignKey() {
            return 'city-offer-marketplace';
        },
        marketplaceCampaign() {
            return _.get(this.activeCms, 'is_business_card', false);
        },
        campaigns() {
            const campaigns = [];
            if (this.widget.data && this.widget.data.length) {
                this.widget.data.forEach(element => {
                    if (!element.campaign_key) return;
                    if (element.campaign_key === this.marketplaceCampaignKey) return;
                    if (!campaigns.find(campaign => campaign.key === element.campaign_key)) {
                        campaigns.push({
                            key: element.campaign_key,
                            name: element.campaign_name,
                        });
                    }
                });
            }
            return campaigns;
        },
        datepickerType() {
            let type;

            switch (this.period) {
                case 'hour':
                    type = 'date'
                    break;

                case 'week':
                    type = 'month'
                    break;

                case 'month':
                    type = 'year'
                    break;

                case 'year':
                    type = 'year'
                    break;

                default:
                // case 'day':
                    type = 'week'
                    break;
            }

            return type;
        },
        datepickerFormat() {
            let format;

            switch (this.period) {
                case 'hour':
                    format = this.$t('yyyy-MM-dd')
                    break;

                case 'week':
                    format = 'MMMM yyyy'
                    break;

                case 'month':
                    format = 'yyyy';
                    break;

                case 'year':
                    format = 'yyyy'
                    break;

                default:
                // case 'day':
                    format = `[${this.$t('Week')}] WW`
                    break;
            }

            return format;
        },
        domainOptions() {
            let options = [];

            const domains = _.get(this.activeCms, 'domains') || [];
            domains.forEach(el => {
                options.push({
                    id: el.id,
                    label: el.base_url
                });
            })

            return options;
        },
        periodOptions() {
            return [
                {
                    key: 'hour',
                    label: this.$t('Hourly'),
                },
                {
                    key: 'day',
                    label: this.$t('Daily'),
                },
                {
                    key: 'week',
                    label: this.$t('Weekly'),
                },
                {
                    key: 'month',
                    label: this.$t('Monthly'),
                },
                {
                    key: 'year',
                    label: this.$t('Yearly'),
                },
            ]
        },
        domain: {
            get() {
               return _.get(this.widget, 'settings.domain') || _.get(this.domainOptions, '0.label')
            },
            set(value) {
                let settings = _.get(this.widget, 'settings') || {}
                settings = _.cloneDeep(settings)

                settings.domain = value

                this.setWidgetValue({
                    widget: this.widget,
                    key: 'settings',
                    value: settings
                });

                this.saveWidget();
                this.refetch();
            }
        },
        daterange: {
            get() {
                let daterange = []

                if (! this.widget.settings.start || ! this.widget.settings.end) {
                    switch (this.period) {
                        case 'hour':
                            daterange = [ startOfDay(appNow()), endOfDay(appNow()) ];
                            break;

                        case 'week':
                            daterange = [ startOfMonth(appNow()), endOfMonth(appNow()) ];
                            break;

                        case 'month':
                            daterange = [ startOfYear(appNow()), endOfYear(appNow()) ];
                            break;

                        case 'year':
                            daterange = [ startOfDecade(appNow()), endOfDecade(appNow()) ];
                            break;

                        default:
                        // case 'day':
                            daterange = [ startOfWeek(appNow(), { weekStartsOn: 1 }), endOfWeek(appNow(), { weekStartsOn: 1 }) ];
                            break;

                    }
                } else {
                    daterange = [ appFromUnixTime(this.widget.settings.start), appFromUnixTime(this.widget.settings.end) ]
                }

                return daterange;
            },
            set(value) {
                if (value && value.length >= 2) {
                    let settings = _.get(this.widget, 'settings') || {}
                    settings = _.cloneDeep(settings)

                    settings.start = appGetUnixTime(value[0])
                    settings.end = appGetUnixTime(endOfDay(value[1]))

                    this.setWidgetValue({
                        widget: this.widget,
                        key: 'settings',
                        value: settings
                    });

                    this.saveWidget();
                    this.refetch();
                }
            }
        },
        date: {
            get() {
                let date = appNow()

                const start = _.get(this.widget, 'settings.start');
                const end = _.get(this.widget, 'settings.end');
                if (start && end) date = appFromUnixTime((start + end) / 2)

                return date
            },
            set(value) {
                let settings = _.get(this.widget, 'settings') || {}
                settings = _.cloneDeep(settings)

                switch (this.period) {
                    case 'hour':
                        settings.start = appGetUnixTime(startOfDay(value, { weekStartsOn: 1 }))
                        settings.end = appGetUnixTime(endOfDay(value, { weekStartsOn: 1 }))
                        break;

                    case 'week':
                        settings.start = appGetUnixTime(startOfWeek(startOfMonth(value), { weekStartsOn: 1 }))
                        settings.end = appGetUnixTime(endOfWeek(endOfMonth(value), { weekStartsOn: 1 }))
                        break;

                    case 'month':
                        settings.start = appGetUnixTime(startOfYear(value, { weekStartsOn: 1 }))
                        settings.end = appGetUnixTime(endOfYear(value, { weekStartsOn: 1 }))
                        break;

                    case 'year':
                        settings.start = appGetUnixTime(startOfDecade(value, { weekStartsOn: 1 }))
                        settings.end = appGetUnixTime(endOfDecade(value, { weekStartsOn: 1 }))
                        break;

                    default:
                    // case 'day':
                        settings.start = appGetUnixTime(startOfWeek(value, { weekStartsOn: 1 }))
                        settings.end = appGetUnixTime(endOfWeek(value, { weekStartsOn: 1 }))
                        break;
                }

                this.setWidgetValue({
                    widget: this.widget,
                    key: 'settings',
                    value: settings
                });

                this.saveWidget();
                this.refetch();
            }
        },
        dateFnsFormat() {
            switch (this.period) {
                case 'hour': return this.$t('yyyy-MM-dd')
                case 'week': return 'MMMM yyyy'
                case 'month': return 'yyyy'
                case 'year': return 'yyyy'
                default: return "'" + this.$t('Week') + "' ww"
            }
        },
        decadeOptions() {
            const currentDecade = Math.floor(appNow().getFullYear() / 10) * 10
            const cmsCreatedAt = _.get(this.activeCms, 'created_at')
            const earliestDecade = cmsCreatedAt
                ? Math.floor(appFromUnixTime(cmsCreatedAt).getFullYear() / 10) * 10
                : currentDecade - 10
            const options = []
            for (let d = currentDecade; d >= earliestDecade; d -= 10) {
                options.push({ value: d, label: d + ' - ' + (d + 9) })
            }
            return options
        },
        selectedDecade: {
            get() {
                const start = _.get(this.widget, 'settings.start')
                if (start) {
                    return Math.floor(appFromUnixTime(start).getFullYear() / 10) * 10
                }
                return Math.floor(appNow().getFullYear() / 10) * 10
            },
            set(value) {
                let settings = _.get(this.widget, 'settings') || {}
                settings = _.cloneDeep(settings)
                const startDate = new Date(value, 0, 1)
                const endDate = new Date(value + 9, 11, 31, 23, 59, 59)
                settings.start = appGetUnixTime(startDate)
                settings.end = appGetUnixTime(endDate)
                this.setWidgetValue({ widget: this.widget, key: 'settings', value: settings })
                this.saveWidget()
                this.refetch()
            },
        },
        dateDisplayLabel() {
            if (this.period === 'year') {
                const start = _.get(this.widget, 'settings.start')
                const end = _.get(this.widget, 'settings.end')
                if (start && end) {
                    return appFromUnixTime(start).getFullYear() + ' - ' + appFromUnixTime(end).getFullYear()
                }
                const decade = Math.floor(appNow().getFullYear() / 10) * 10
                return decade + ' - ' + (decade + 9)
            }
            return formatDate(this.date, this.dateFnsFormat)
        },
        periodLabel() {
            const period = this.period
            const option = this.periodOptions.find(o => o.key === period)
            return option ? option.label : period
        },
        period: {
            get() {
                return _.get(this.widget, 'settings.period') || 'day'
            },
            set(value) {
                let settings = _.get(this.widget, 'settings') || {}
                settings = _.cloneDeep(settings)

                settings.period = value

                const midpoint = appFromUnixTime((this.widget.settings.start + this.widget.settings.end) / 2);
                switch (settings.period) {
                    case 'hour':
                        settings.start = appGetUnixTime(startOfDay(midpoint, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfDay(midpoint, { weekStartsOn: 1 }));
                        break;

                    case 'week':
                        settings.start = appGetUnixTime(startOfWeek(startOfMonth(midpoint), { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfWeek(endOfMonth(midpoint), { weekStartsOn: 1 }));
                        break;

                    case 'month':
                        settings.start = appGetUnixTime(startOfYear(midpoint, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfYear(midpoint, { weekStartsOn: 1 }));
                        break;

                    case 'year':
                        settings.start = appGetUnixTime(startOfDecade(midpoint, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfDecade(midpoint, { weekStartsOn: 1 }));
                        break;

                    default:
                    // case 'day':
                        settings.start = appGetUnixTime(startOfWeek(midpoint, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfWeek(midpoint, { weekStartsOn: 1 }));
                        break;
                }

                this.setWidgetValue({
                    widget: this.widget,
                    key: 'settings',
                    value: settings
                });

                this.saveWidget();
                this.refetch();
            }
        },
        isNextDisabled() {
            let disabled = this.date > appNow();

            switch (this.period) {
                case 'hour':
                    disabled = addDays(startOfDay(this.date), 1) > appNow();
                    break;

                case 'week':
                    disabled = addMonths(startOfMonth(this.date), 1) > appNow();
                    break;

                case 'month':
                    disabled = addYears(startOfYear(this.date), 1) > appNow();
                    break;

                case 'year':
                    disabled = addYears(startOfDecade(this.date), 10) > appNow();
                    break;

                default:
                // case 'day':
                    disabled = addWeeks(startOfWeek(this.date), 1) > appNow();
                    break;
            }

            return disabled;
        },
        allowedToUpdate() {
            let allowed = true;
            if (! this.isSuperUser && ! this.isOwner) {
                allowed = this.rights.find(right => right.key === this.right && right.permission.update)
            }
            return allowed;
        },
        isSuperUser() {
            return useAuthStore().isSuperUser;
        },
        isOwner() {
            return useAuthStore().isOwner;
        },
        rights() {
            return useAuthStore().rights;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
    },
    methods: {
        onCustomNameChange(value) {
            useDashboardStore().setWidgetCustomName(this.widget.key, value);
        },
        syncSettingsField(field, value) {
            let settings = _.get(this.widget, 'settings') || {};
            settings = _.cloneDeep(settings);
            settings[field] = value;
            this.setWidgetValue({ widget: this.widget, key: 'settings', value: settings });
        },
        forceRefetch() {
            apolloClient.clearStore().then(() => {
                setTimeout(this.refetch, 10);
            })
        },
        handleClick(event, elements) {
            if (this.period !== 'hour' && elements && elements.length) {
                const element = elements[0]
                // Chart.js 3: use element.index instead of element._index
                const i = element.index;

                let settings = _.get(this.widget, 'settings') || {}
                settings = _.cloneDeep(settings)

                let period, date;
                switch (settings.period) {
                    case 'week':
                        period = 'day';
                        date = addWeeks(appFromUnixTime(settings.start), i);
                        settings.start = appGetUnixTime(startOfWeek(date, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfWeek(date, { weekStartsOn: 1 }));
                        break;

                    case 'month':
                        period = 'week';
                        date = addMonths(appFromUnixTime(settings.start), i);
                        settings.start = appGetUnixTime(startOfWeek(startOfMonth(date), { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfWeek(endOfMonth(date), { weekStartsOn: 1 }));
                        break;

                    case 'year':
                        period = 'month';
                        date = addYears(appFromUnixTime(settings.start), i);
                        settings.start = appGetUnixTime(startOfYear(date, { weekStartsOn: 1 }));
                        settings.end = appGetUnixTime(endOfYear(date, { weekStartsOn: 1 }));
                        break;

                    default:
                    // case 'day':
                        period = 'hour';
                        date = addDays(appFromUnixTime(settings.start), i);
                        settings.start = appGetUnixTime(startOfDay(date));
                        settings.end = appGetUnixTime(endOfDay(date));
                        break;
                }

                settings.period = period

                this.setWidgetValue({
                    widget: this.widget,
                    key: 'settings',
                    value: settings
                });

                this.saveWidget();
                this.refetch();
            }
        },
        async forceUpdate() {
            this.loading = true;

            await apolloClient.clearStore();

            StatisticsApi.generateSiteviewStatistics({ cms: this.activeCms.id })
                .then(() => {
                    this.$notify.success({
                        title: this.$t('The summary will be processed asynchronously'),
                        message: this.$t('You will be notified once the summary is completed'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 3000
                    });
                })
                .finally(() => {
                    this.loading = false;
                })
        },
        refetch(loading = true) {
            this.loading = loading;

            let filters = {
                cms: this.activeCms.id,
                domain: _.get(this.widget, 'settings.domain') || _.get(this.domainOptions, '0.label'),
                period: _.get(this.widget, 'settings.period') || 'day',
                from: _.get(this.widget, 'settings.start') || appGetUnixTime(startOfDay(appNow())),
                to: _.get(this.widget, 'settings.end') || appGetUnixTime(endOfDay(appNow())),
            };

            StatisticsApi.fetchSiteviewStatistics(filters)
                .then(data => {
                    this.error = false;

                    this.setWidgetValue({
                        widget: this.widget,
                        key: 'data',
                        value: data
                    });

                    this.$nextTick(() => {
                        this.generate();
                        if (this.$refs.chart) {
                            this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
                            setTimeout(() => this.chartOptions.animation.duration = 0, 1000);
                        }
                    })
                })
                .catch(err => {
                    this.datacollection = undefined;

                    const { graphQLErrors } = err

                    let code = 501;
                    let message = 'An error occurred!';

                    if (graphQLErrors) {
                        code = _.get(graphQLErrors, '0.code') || code;
                        message = _.get(graphQLErrors, '0.message') || message;
                    }

                    switch (code) {
                        case 401:
                            this.error = { code, message: 'Unauthorized' };
                            break;
                        case 403:
                            this.error = { code, message: 'Not Allowed' };
                            break;
                        // case 404:
                        //     break;
                        default:
                            this.error = true;
                            break;
                    }

                    /* eslint no-console: ["error", { allow: ["error"] }] */
                    console.error(err);
                })
                .finally(() => {
                    this.loading = false
                })

        },
        generate() {
            let addMethod;
            let differenceMethod;
            let periodStartMethod;

            let chartStartMethod;
            let chartEndMethod;

            let format = this.$t('yyyy-MM-dd');

            let data = []
            let labels = []

            let from = startOfWeek(appNow(), { weekStartsOn: 1 })
            const start = _.get(this.widget, 'settings.start');
            if (start) from = appFromUnixTime(start)

            let to = endOfWeek(appNow(), { weekStartsOn: 1 })
            const end = _.get(this.widget, 'settings.end');
            if (end) to = appFromUnixTime(end)

            switch (this.period) {
                case 'hour':
                    differenceMethod = differenceInHours;
                    periodStartMethod = startOfHour;
                    chartStartMethod = startOfDay;
                    chartEndMethod = endOfDay;
                    addMethod = addHours;
                    format = 'HH:mm';
                    break;

                case 'week':
                    differenceMethod = differenceInWeeks;
                    periodStartMethod = date => startOfWeek(date, {weekStartsOn: 1});
                    chartStartMethod = startOfMonth;
                    chartEndMethod = endOfMonth;
                    addMethod = addWeeks;
                    format = `'${this.$t('Week')}' ww`;
                    break;

                case 'month':
                    differenceMethod = differenceInMonths;
                    periodStartMethod = startOfMonth;
                    chartStartMethod = startOfYear;
                    chartEndMethod = endOfYear;
                    addMethod = addMonths;
                    format = 'LLLL yyyy';
                    break;

                case 'year':
                    differenceMethod = differenceInYears;
                    periodStartMethod = startOfYear;
                    chartStartMethod = startOfDecade;
                    chartEndMethod = endOfDecade;
                    addMethod = addYears;
                    format = 'yyyy';
                    break;

                default:
                // case 'day':
                    differenceMethod = differenceInDays;
                    periodStartMethod = startOfDay;
                    chartStartMethod = date => startOfWeek(date, {weekStartsOn: 1});
                    chartEndMethod = date => endOfWeek(date, {weekStartsOn: 1});
                    addMethod = addDays;
                    break;
            }

            if (from && to) {
                let current = from;
                while (current <= to) {
                    const label = formatDate(current, format);
                    labels.push(label);

                    // if (current > appNow()) data.push(NaN);
                    // else data.push(0)

                    data.push(null)

                    current = addMethod(current, 1);
                }
            }

            // this.datacollection = {
            //     labels: ['foo', 'bar', 'baz'],
            //     datasets: [
            //         {
            //             key: 'all-visitors',
            //             hidden: this.disabledDatasets.indexOf('all-visitors') >= 0,
            //             label: this.$t('Unique Visitors'),
            //             // borderWidth: 1,
            //             borderColor: 'rgb(190, 21, 33)',
            //             backgroundColor: 'rgba(190, 21, 33, 0.7)',
            //             fill: false,
            //             // borderDash: [10,10],
            //             // lineTension: 0.4,
            //             pointHitRadius: 30,
            //             pointRadius: 3,
            //             pointHoverRadius: 7,
            //             data: [Math.floor(Math.random() * (20 - 2 + 1) + 2)]
            //         },
            //     ],
            // }

            this.datacollection = {
                labels,
                datasets: [
                    {
                        key: 'all-visitors',
                        hidden: this.disabledDatasets.indexOf('all-visitors') >= 0,
                        label: this.$t('Unique Visitors'),
                        // borderWidth: 1,
                        borderColor: 'rgb(0, 0, 0)',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        fill: false,
                        // borderDash: [10,10],
                        // lineTension: 0.4,
                        pointHitRadius: 30,
                        pointRadius: 3,
                        pointHoverRadius: 7,
                        data: _.cloneDeep(data),
                        spanGaps: true,
                    },
                    {
                        key: 'all-pageviews',
                        hidden: this.disabledDatasets.indexOf('all-pageviews') >= 0,
                        label: this.$t('Siteviews'),
                        borderColor: 'rgb(160, 160, 160)',
                        backgroundColor: 'rgba(160, 160, 160, 0.7)',
                        fill: false,
                        // borderDash: [10,10],
                        // lineTension: 0.4,
                        pointHitRadius: 30,
                        pointRadius: 3,
                        pointHoverRadius: 7,
                        data: _.cloneDeep(data),
                        spanGaps: true,
                    },
                    {
                        key: 'city-offer-marketplace:visitors',
                        hidden: this.campaignVisitorsVisible !== true || this.disabledDatasets.indexOf('city-offer-marketplace:visitors') >= 0,
                        label: this.$t('Unique Visitors') + ' (' + this.$t('CityOffer Marketplace') + ')',
                        borderColor: '#be1521',
                        backgroundColor: Color('#be1521').fade(0.3).rgb().string(),
                        fill: false,
                        pointHitRadius: 30,
                        pointRadius: 3,
                        pointHoverRadius: 7,
                        data: _.cloneDeep(data),
                        spanGaps: true,
                    },
                    {
                        key: 'city-offer-marketplace:views',
                        hidden: this.campaignViewsVisible !== true || this.disabledDatasets.indexOf('city-offer-marketplace:views') >= 0,
                        label: this.$t('Siteviews') + ' (' + this.$t('CityOffer Marketplace') + ')',
                        borderColor: '#95c01f',
                        backgroundColor: Color('#95c01f').fade(0.3).rgb().string(),
                        fill: false,
                        pointHitRadius: 30,
                        pointRadius: 3,
                        pointHoverRadius: 7,
                        data: _.cloneDeep(data),
                        spanGaps: true,
                    },
                ],
            }

            const now = differenceMethod(periodStartMethod(appNow()), chartStartMethod(appNow()))
            const chartIsInThePast = isPast(chartEndMethod(this.daterange[1]))

            const siteviews = _.get(this.widget, 'data')
            if (siteviews && siteviews.length) {

                let i = 0;
                siteviews.forEach(el => {
                    i = differenceMethod(appFromUnixTime(el.start), periodStartMethod(from))

                    if (el.campaign_key && (el.campaign_key === 'city-offer-marketplace' || this.enabledExtensions.includes('marketing'))) {
                        const hex = _.get(el.campaign, 'color') || '#6b52ef';

                        const visitorsDatasetIndex = this.datacollection.datasets.findIndex(dataset => {
                            return dataset.key === `${el.campaign_key}:visitors`;
                        });

                        if (visitorsDatasetIndex >= 0) {
                            // let visitorsData = this.datacollection.datasets[visitorsDatasetIndex].data[i];
                            // if (visitorsData === undefined || isNaN(visitorsData)) visitorsData = 0;
                            if (this.datacollection.datasets[visitorsDatasetIndex].data[i] === null) {
                                this.datacollection.datasets[visitorsDatasetIndex].data[i] = 0;
                            }
                            this.datacollection.datasets[visitorsDatasetIndex].data[i] += el.visitors;
                        } else {
                            const visitorsDataset = {
                                key: `${el.campaign_key}:visitors`,
                                hidden: this.campaignVisitorsVisible !== true || this.disabledDatasets.indexOf(`${el.campaign_key}:visitors`) >= 0,
                                label:  this.$t('Unique Visitors') + ` (${el.campaign_name})`,
                                borderColor: hex,
                                backgroundColor: Color(hex).fade(0.3).rgb().string(),
                                fill: false,
                                // borderDash: [10,10],
                                // lineTension: 0.4,
                                pointHitRadius: 30,
                                pointRadius: 3,
                                pointHoverRadius: 7,
                                data: _.cloneDeep(data),
                                spanGaps: true,
                            };
                            visitorsDataset.data[i] = el.visitors;
                            this.datacollection.datasets.push(visitorsDataset);
                        }

                        const viewsDatasetIndex = this.datacollection.datasets.findIndex(dataset => {
                            return dataset.key === `${el.campaign_key}:views`;
                        });

                        if (viewsDatasetIndex >= 0) {
                            // let viewsData = this.datacollection.datasets[viewsDatasetIndex].data[i];
                            // if (viewsData === undefined || isNaN(viewsData)) viewsData = 0;
                            if (this.datacollection.datasets[viewsDatasetIndex].data[i] === null) {
                                this.datacollection.datasets[viewsDatasetIndex].data[i] = 0;
                            }
                            this.datacollection.datasets[viewsDatasetIndex].data[i] += el.views;
                        } else {
                            const viewsDataset = {
                                key: `${el.campaign_key}:views`,
                                hidden: this.campaignViewsVisible !== true || this.disabledDatasets.indexOf(`${el.campaign_key}:views`) >= 0,
                                label:  this.$t('Siteviews') + ` (${el.campaign_name})`,
                                borderColor: Color(hex).fade(0.5).rgb().string(),
                                backgroundColor: Color(hex).fade(0.5).fade(0.3).rgb().string(),
                                fill: false,
                                // borderDash: [10,10],
                                // lineTension: 0.4,
                                pointHitRadius: 30,
                                pointRadius: 3,
                                pointHoverRadius: 7,
                                data: _.cloneDeep(data),
                                spanGaps: true,
                            };
                            viewsDataset.data[i] = el.views;
                            this.datacollection.datasets.push(viewsDataset);
                        }
                    }

                    // Hauptdatensätze aktualisieren
                    if (this.datacollection.datasets[0].data[i] === null) {
                        this.datacollection.datasets[0].data[i] = 0;
                    }
                    this.datacollection.datasets[0].data[i] += el.visitors;

                    if (this.datacollection.datasets[1].data[i] === null) {
                        this.datacollection.datasets[1].data[i] = 0;
                    }
                    this.datacollection.datasets[1].data[i] += el.views;
                })
            }

            // fix gaps
            this.datacollection.datasets.forEach((dataset, datasetIndex) => {
                dataset.data.forEach((el, index) => {
                    if (!el && (chartIsInThePast || index <= now)) {
                        this.datacollection.datasets[datasetIndex].data[index] = 0
                    }
                })
            })
        },
        // getRandomInt () {
        //     return Math.floor(Math.random() * (50 - 5 + 1)) + 5
        // },
        // updateWindowWidth() {
        //     this.windowWidth = window.outerWidth
        // },
        disabledDate(date) {
            let disabled = false;

            // return date > appNow();
            switch (this.period) {
                case 'hour':
                    disabled = date > endOfDay(appNow());
                    break;

                case 'week':
                    disabled = date > endOfMonth(appNow());
                    break;

                case 'month':
                    disabled = date > endOfYear(appNow());
                    break;

                case 'year':
                    disabled = date > endOfDecade(appNow());
                    break;

                default:
                // case 'day':
                    disabled = date > endOfWeek(appNow(), { weekStartsOn: 1 })
                    break;
            }

            return disabled;
        },
        next() {
            let date = this.date

            switch (this.period) {
                case 'hour':
                    date = addDays(this.date, 1);
                    break;

                case 'week':
                    date = addMonths(this.date, 1);
                    break;

                case 'month':
                    date = addYears(this.date, 1);
                    break;

                case 'year':
                    date = addYears(this.date, 10);
                    break;

                default:
                // case 'day':
                    date = addWeeks(this.date, 1);
                    break;
            }

            this.date = date
        },
        prev() {
            let date = this.date

            switch (this.period) {
                case 'hour':
                    date = subDays(this.date, 1);
                    break;

                case 'week':
                    date = subMonths(this.date, 1);
                    break;

                case 'month':
                    date = subYears(this.date, 1);
                    break;

                case 'year':
                    date = subYears(this.date, 10);
                    break;

                default:
                // case 'day':
                    date = subWeeks(this.date, 1);
                    break;
            }

            this.date = date
        },
        getLegendStyle(key, disabled = false) {
            let style = {};

            if (this.datacollection) {
                const datasets = this.datacollection.datasets || []
                const dataset = datasets.find(el => el.key === key)
                if (disabled) {
                    style['background-color'] = 'rgba(0,0,0, 0.1)'
                    style['border-color'] = 'rgba(0,0,0, 0.15)'
                } else if (dataset) {
                    style['background-color'] = dataset.backgroundColor
                    style['border-color'] = dataset.borderColor
                }
            }

            return style;
        },
        toggleDatasets(keys, event) {
            if (event && (event.ctrlKey || event.metaKey)) {
                if (this.datacollection) {
                    let disabledDatasets = this.datacollection.datasets.map(el => {
                        if (keys.includes(el.key) === false) return el.key;
                    });

                    if (disabledDatasets.filter(key => this.disabledDatasets.includes(key)).length < disabledDatasets.length) {
                        this.disabledDatasets = disabledDatasets;
                    } else {
                        this.disabledDatasets = [];
                    }
                }
            } else {
                keys.forEach(key => {
                    const index = this.disabledDatasets.indexOf(key);
                    if (index >= 0) this.disabledDatasets.splice(index, 1);
                    else this.disabledDatasets.push(key)
                })
            }

            // if (this.datacollection) {
            //     const datasets = this.datacollection.datasets || []
            //     datasets.forEach(el => {
            //         if (this.disabledDatasets.includes(el.key)) el.hidden = true;
            //         else el.hidden = false;
            //     })
            // }

            this.generate();
            if (this.$refs.chart) {
                this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
            }
        },
        toggleCampaignVisitors() {
            this.campaignVisitorsVisible = ! this.campaignVisitorsVisible;

            if (! this.campaignVisitorsVisible && ! this.campaignViewsVisible) this.campaignViewsVisible = true

            this.generate();
            if (this.$refs.chart) {
                this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
            }
        },
        toggleCampaignViews() {
            this.campaignViewsVisible = ! this.campaignViewsVisible;

            if (! this.campaignViewsVisible && ! this.campaignVisitorsVisible) this.campaignVisitorsVisible = true

            this.generate();
            if (this.$refs.chart) {
                this.$refs.chart.renderChart(this.datacollection, this.chartOptions)
            }
        },
        // initializeStatusLogSubscription() {
        //     if (!echo.client) {
        //         setTimeout(() => this.initializeStatusLogSubscription(), 10)
        //     } else {
        //         this.fetchRelatedStatusLogs();
        //
        //         echo.client.private('cms.' + this.activeCms.id)
        //             .listen('.status-log:created', async data => {
        //                 if (data.log && this.logKeys.includes(data.log.key)) {
        //                     // check if log is already in the list
        //                     const exists = this.logs.find(log => parseInt(log.id) === parseInt(data.log.id));
        //
        //                     // if not, fetch it and add it to the list
        //                     if (!exists) {
        //                         const logs = await StatusLogApi.fetchList({
        //                             id: data.log.id,
        //                             cms: parseInt(this.activeCms.id),
        //                             status: ['info'],
        //                             key: this.logKeys,
        //                             hide: false,
        //                         })
        //                         if (logs && logs.length) {
        //                             this.logs = [...logs, ...this.logs]
        //
        //                             // order by created_at timestamp desc
        //                             this.logs = _.orderBy(this.logs, 'created_at', 'desc')
        //                             this.logs = _.uniqBy(this.logs, 'id');
        //                         }
        //                     }
        //                 }
        //             })
        //             .listen('.status-log:updated', async data => {
        //                 if (data.log && this.logKeys.includes(data.log.key)) {
        //                     // find the log in the list
        //                     // and update it
        //                     const index = this.logs.findIndex(log => parseInt(log.id) === parseInt(data.log.id));
        //
        //                     if (index >= 0) {
        //                         // this.$set(this.logs, index, data.log);
        //                         if (data.log.hide) {
        //                             this.logs = this.logs.filter(log => parseInt(log.id) !== parseInt(data.log.id));
        //                         } else {
        //                             this.$set(this.logs[index], 'status', data.log.status);
        //                             this.$set(this.logs[index], 'progress', data.log.progress);
        //                             this.$set(this.logs[index], 'updated_at', data.log.updated_at);
        //                             this.$set(this.logs[index], 'heading', data.log.heading);
        //                             this.$set(this.logs[index], 'body', data.log.body);
        //                             this.$set(this.logs[index], 'data', data.log.data);
        //                             this.$set(this.logs[index], 'hide', data.log.hide);
        //
        //                             if (data.log.status !== 'info') {
        //                                 setTimeout(() => {
        //                                     this.logs = this.logs.filter(log => parseInt(log.id) !== parseInt(data.log.id));
        //                                 }, 3000)
        //                             }
        //                         }
        //                     } else {
        //                         // if not found, fetch it and add it to the list
        //                         const logs = await StatusLogApi.fetchList({
        //                             id: data.log.id,
        //                             cms: parseInt(this.activeCms.id),
        //                             status: ['info'],
        //                             key: this.logKeys,
        //                             hide: false,
        //                         })
        //                         // if (logs && logs.length) {
        //                         //     // find the right place to insert the new log
        //                         //     const i = this.logs.findIndex(log => log.id > data.log.id)
        //                         //     if (i >= 0) this.logs.splice(i, 0, logs[0])
        //                         //     else this.logs.push(logs[0])
        //                         // }
        //                         if (logs && logs.length) {
        //                             this.logs = [...logs, ...this.logs]
        //
        //                             // order by created_at timestamp desc
        //                             this.logs = _.orderBy(this.logs, 'created_at', 'desc')
        //                             this.logs = _.uniqBy(this.logs, 'id');
        //                         }
        //                     }
        //                 }
        //             })
        //     }
        // },

        // fetchRelatedStatusLogs() {
        //     let variables = {
        //         cms: parseInt(this.activeCms.id),
        //         status: ['info'],
        //         key: this.logKeys,
        //         hide: false,
        //         skip: 0,
        //         take: 10,
        //     };
        //
        //     StatusLogApi.fetchList(variables)
        //         .then((logs) => this.logs = logs)
        //         .catch(err => console.error(err));
        // },
        // hideStatusLog(log) {
        //     this.loadingLogs = true;
        //
        //     log = _.cloneDeep(log);
        //     if (log.file) delete log.file
        //     log.hide = true;
        //
        //     StatusLogApi.update(log)
        //         .then(data => {
        //             // remove from list
        //             if (data.hide) {
        //                 this.logs = this.logs.filter(el => el.id !== log.id);
        //             } else {
        //                 this.$notify.error({
        //                     title: this.$t('An error occurred!'),
        //                     message: this.$t('Status log could not be hidden.'),
        //                     iconClass: 'fas el-icon-fa-times',
        //                     position: 'bottom-left',
        //                     customClass: 'dark',
        //                     duration: 8000
        //                 });
        //             }
        //         })
        //         .catch(err => {
        //             console.error(err)
        //             this.$notify.error({
        //                 title: this.$t('An error occurred!'),
        //                 message: this.$t('Status log could not be hidden.'),
        //                 iconClass: 'fas el-icon-fa-times',
        //                 position: 'bottom-left',
        //                 customClass: 'dark',
        //                 duration: 8000
        //             });
        //         })
        //         .finally(() => {
        //             this.loadingLogs = false
        //         });
        // },

        setWidgetValue(payload) {
            return useDashboardStore().setWidgetValue(payload);
        },
        saveWidget() {
            return useDashboardStore().saveWidget();
        }
    },
    mounted () {
        // this.updateWindowWidth();

        this.chartOptions.animation.duration = 1000
        this.refetch();

        // this.initializeStatusLogSubscription();
        // this.fetchRelatedStatusLogs();

        // window.addEventListener('resize', this.updateWindowWidth);

        // echo.client.private('cms.' + state.activeCms.id)
        //     .listen('.job:updated', data => {
        //         // if (data.job && ['export:stock'].includes(data.job.type)) {
        //         //     commit('updateUnfinishedJob', data.job);
        //         // }
        //     })
    },
    beforeDestroy() {
        // window.removeEventListener('resize', this.updateWindowWidth);

        // echo.client.private(`cms.${this.activeCms.id}`)
        //     .stopListening('.job:updated')

        if (echo.client) {
            echo.client.private(`cms.${this.activeCms.id}`)
                .stopListening('.status-log:created')
                .stopListening('.status-log:updated')
        }
    },
}
</script>

<style lang="sass" scoped>
@import "@/views/cms/Dashboard/widgets/_widget.sass"

.chart-container
    display: flex
    flex-direction: column
    min-height: 0
    .chart
        flex-grow: 1
        min-height: 0
    &:hover
        .prev, .next
            opacity: 1
            transform: scale(1)

.chart-container-disabled
    display: flex
    flex-direction: column
    flex-grow: 1
    p.body
        flex-grow: 1
        display: flex
        justify-content: center
        align-items: center
        font-size: 2rem
        font-weight: 200
        color: #c3c3c3

.legend
    .campaigns
        width: 100%
        display: flex
        justify-content: space-between
    ul li
        &.campaign .el-button
            padding-left: 25px
            span.secondary-color
                opacity: 1
                display: inline-block
                position: absolute
                width: 12px
                height: 12px
                top: 11px
                left: 11px
                border-radius: 50%
                // border: 1px solid
                outline: 1px solid #ffffff
                box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.07), 0 1px 7px 0 rgba(0, 0, 0, 0.06), 0 3px 1px -1px rgba(0, 0, 0, 0.1)
            span.color:after
                content: ''
                position: absolute
                background: white
                top: 6px
                left: 10px
                width: 14px
                height: 14px
                border-radius: 50%

.job
    .summary
        display: flex
        justify-content: space-between
</style>
